import React, { useState } from "react";
import { Plus, Minus, Search, Copy, Archive, Pencil, Eye, Upload, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useList, useCreate, useUpdate, useBulkDelete } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState, StatusBadge, ProgressBar, fmt } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const STATUSES = ["Not Started", "In Production", "On Hold", "Delayed", "Completed", "Ready to Dispatch", "Dispatched"];
const PRIORITIES = ["Low", "Medium", "High", "Urgent"];
const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

function OrderForm({ open, onClose, editing, products, customers }) {
  const blank = {
    po_number: "", buyer: "", brand: "", product_id: "", product_name: "", sku: "", category: "",
    fabric_type: "", fabric_gsm: "", color: "", priority: "Medium", status: "Not Started",
    delivery_date: "", production_start_date: "", daily_target: "", product_image: "", notes: "",
    size_breakdown: SIZES.map((s) => ({ size: s, quantity: 0 })),
  };
  const [form, setForm] = useState(editing || blank);
  const [uploading, setUploading] = useState(false);
  const create = useCreate("/orders", ["orders", "dashboard"]);
  const update = useUpdate((id) => `/orders/${id}`, ["orders", "dashboard"]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSize = (i, v) => setForm((f) => {
    const sb = [...f.size_breakdown]; sb[i] = { ...sb[i], quantity: Number(v) || 0 }; return { ...f, size_breakdown: sb };
  });
  const total = (form.size_breakdown || []).reduce((a, s) => a + (Number(s.quantity) || 0), 0);

  const onProduct = (pid) => {
    const p = products.find((x) => x.id === pid);
    if (p) setForm((f) => ({ ...f, product_id: p.id, product_name: p.name, sku: p.sku, category: p.category,
      fabric_type: p.fabric, fabric_gsm: p.gsm, color: p.default_color, product_image: p.image_url || "" }));
  };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("product_image", `${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.product_name) return toast.error("Select a product");
    const payload = { ...form, order_quantity: total };
    try {
      if (editing) await update.mutateAsync({ id: editing.id, body: payload });
      else await create.mutateAsync(payload);
      toast.success(editing ? "Order updated" : "Order created");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-form">
        <DialogHeader><DialogTitle>{editing ? `Edit ${editing.order_id}` : "New Order"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Product</Label>
            <Select value={form.product_id} onValueChange={onProduct}>
              <SelectTrigger data-testid="order-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
              <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Buyer / Customer</Label>
            <Select value={form.buyer} onValueChange={(v) => set("buyer", v)}>
              <SelectTrigger data-testid="order-buyer-select"><SelectValue placeholder="Select buyer" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>PO Number</Label><Input value={form.po_number} onChange={(e) => set("po_number", e.target.value)} data-testid="order-po-input" /></div>
          <div><Label>Brand</Label><Input value={form.brand} onChange={(e) => set("brand", e.target.value)} /></div>
          <div><Label>Color</Label><Input value={form.color} onChange={(e) => set("color", e.target.value)} /></div>
          <div><Label>Fabric GSM</Label><Input value={form.fabric_gsm} onChange={(e) => set("fabric_gsm", e.target.value)} /></div>
          <div>
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Production Start</Label><Input type="date" value={form.production_start_date || ""} onChange={(e) => set("production_start_date", e.target.value)} /></div>
          <div><Label>Delivery Date</Label><Input type="date" value={form.delivery_date || ""} onChange={(e) => set("delivery_date", e.target.value)} data-testid="order-delivery-input" /></div>
          <div><Label>Daily Target</Label><Input type="number" value={form.daily_target} onChange={(e) => set("daily_target", e.target.value)} /></div>
          <div>
            <Label>Product Image</Label>
            <div className="flex items-center gap-2">
              <input type="file" accept="image/*" onChange={upload} className="hidden" id="ord-img" />
              <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById("ord-img").click()} disabled={uploading} className="gap-1">
                <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload"}
              </Button>
              {form.product_image && <img src={form.product_image} alt="" className="w-9 h-9 rounded object-cover" />}
            </div>
          </div>
        </div>

        <div className="mt-2">
          <Label>Size Breakdown — Total: <span className="font-num font-bold text-primary" data-testid="order-size-total">{fmt(total)}</span></Label>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-1.5">
            {form.size_breakdown.map((s, i) => (
              <div key={s.size}>
                <div className="text-xs text-center text-muted-foreground font-semibold">{s.size}</div>
                <Input type="number" value={s.quantity} onChange={(e) => setSize(i, e.target.value)} className="text-center font-num" data-testid={`order-size-${s.size}`} />
              </div>
            ))}
          </div>
        </div>
        <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} data-testid="order-save-btn" disabled={create.isPending || update.isPending}>
            {editing ? "Save Changes" : "Create Order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Orders() {
  const { canWrite, user } = useAuth();
  const clearAll = useBulkDelete((o) => `/orders/${o.id}`, ["orders", "dashboard"]);
  const [status, setStatus] = useState("all");
  const changeStage = async (o, stage) => {
    try {
      await api.put(`/orders/${o.id}`, { current_stage: stage, current_department: stage });
      toast.success(`${o.order_id} → ${stage}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update stage"); }
  };
  const adjustProgress = async (o, delta) => {
    try { await api.post(`/orders/${o.id}/progress`, { delta }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to update progress"); }
  };
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(null); // 'new' | order
  const { data: orders, isLoading } = useList("orders", "/orders", { status: status === "all" ? undefined : status, search: search || undefined });
  const { data: products = [] } = useList("products", "/products");
  const { data: customers = [] } = useList("customers", "/customers");
  const { data: stages = [] } = useList("stages", "/stages");

  const dup = async (o) => { try { await api.post(`/orders/${o.id}/duplicate`); toast.success("Order duplicated"); } catch { toast.error("Failed"); } };
  const archive = async (o) => { if (!window.confirm(`Archive ${o.order_id}?`)) return; try { await api.delete(`/orders/${o.id}`); toast.success("Archived"); } catch { toast.error("Failed"); } };

  return (
    <div>
      <PageHeader title="Order Management" subtitle="Create, track and manage production orders" testid="orders-header">
        {canWrite("orders") && (
          <Button onClick={() => setDialog("new")} data-testid="new-order-btn" className="gap-2"><Plus className="w-4 h-4" /> New Order</Button>
        )}
      </PageHeader>

      <Card className="p-3 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search order id, product, buyer, PO…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="orders-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="sm:w-52" data-testid="orders-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>

      <Card className="overflow-hidden">
        {isLoading ? <Loading /> : !orders?.length ? (
          <EmptyState title="No orders found" hint="Create your first order or adjust filters." testid="orders-empty" />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead><TableHead>Product</TableHead><TableHead>Buyer</TableHead>
                  <TableHead className="text-right">Qty</TableHead><TableHead className="w-52">Progress</TableHead>
                  <TableHead>Delivery</TableHead><TableHead>Priority</TableHead><TableHead>Stage</TableHead><TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((o) => (
                  <TableRow key={o.id} data-testid={`order-row-${o.order_id}`} className="hover:bg-secondary/40">
                    <TableCell className="font-num font-semibold text-primary">{o.order_id}</TableCell>
                    <TableCell className="max-w-[180px] truncate">{o.product_name}<div className="text-xs text-muted-foreground">{o.sku}</div></TableCell>
                    <TableCell className="max-w-[130px] truncate">{o.buyer}</TableCell>
                    <TableCell className="text-right font-num">{fmt(o.order_quantity)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        {canWrite("orders") && <Button size="icon" variant="outline" className="h-6 w-6 shrink-0" onClick={() => adjustProgress(o, -10)} data-testid={`progress-minus-${o.order_id}`}><Minus className="w-3 h-3" /></Button>}
                        <ProgressBar value={o.completion_pct} />
                        {canWrite("orders") && <Button size="icon" variant="outline" className="h-6 w-6 shrink-0" onClick={() => adjustProgress(o, 10)} data-testid={`progress-plus-${o.order_id}`}><Plus className="w-3 h-3" /></Button>}
                        <span className="text-xs font-num w-10 text-right">{o.completion_pct}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{o.delivery_date || "—"}</TableCell>
                    <TableCell><span className="text-xs font-medium">{o.priority}</span></TableCell>
                    <TableCell>
                      {canWrite("orders") ? (
                        <Select value={o.current_stage || ""} onValueChange={(v) => changeStage(o, v)}>
                          <SelectTrigger className="h-8 w-40 text-xs" data-testid={`order-stage-${o.order_id}`}><SelectValue placeholder="Set stage" /></SelectTrigger>
                          <SelectContent>{stages.filter((s) => s.active).map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : <span className="text-xs">{o.current_stage || "—"}</span>}
                    </TableCell>
                    <TableCell><StatusBadge status={o.status} /></TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        {canWrite("orders") && <Button size="icon" variant="ghost" onClick={() => setDialog(o)} data-testid={`edit-order-${o.order_id}`}><Pencil className="w-4 h-4" /></Button>}
                        {canWrite("orders") && <Button size="icon" variant="ghost" onClick={() => dup(o)} title="Duplicate" data-testid={`duplicate-order-${o.order_id}`}><Copy className="w-4 h-4" /></Button>}
                        {canWrite("orders") && <Button size="icon" variant="ghost" onClick={() => archive(o)} title="Archive" data-testid={`archive-order-${o.order_id}`}><Archive className="w-4 h-4" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {dialog && (
        <OrderForm
          open={!!dialog}
          onClose={() => setDialog(null)}
          editing={dialog === "new" ? null : dialog}
          products={products}
          customers={customers}
        />
      )}
    </div>
  );
}
